import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { createUser, deleteUser, listUsers, setUserStatus, updateUser } from './api';
import type { CreateUserBody, UpdateUserBody, UserListParams, UserStatus } from './types';

export const adminUsersKeys = {
  all: ['admin-users'] as const,
  list: (params: UserListParams) => [...adminUsersKeys.all, 'list', params] as const,
};

/** Query-hook surface for the feature; Phase 3 mutations invalidate via `adminUsersKeys`.
 *  The Phase-1 list page drives the table via `makeListRequest(listUsers)` directly. */
export function useUsersList(params: UserListParams) {
  return useQuery({ queryKey: adminUsersKeys.list(params), queryFn: () => listUsers(params) });
}

/** Onboard an internal user, then invalidate the list so it refreshes instantly (D8, no polling). */
export function useCreateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateUserBody) => createUser(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: adminUsersKeys.all }),
  });
}

/** Edit an internal user (changed fields only), then invalidate the list so it refreshes (D8). */
export function useUpdateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateUserBody }) => updateUser(id, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: adminUsersKeys.all }),
  });
}

/** Enable/Disable a user, then invalidate the list so it refreshes (D8). */
export function useSetUserStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: UserStatus }) => setUserStatus(id, status),
    onSuccess: () => qc.invalidateQueries({ queryKey: adminUsersKeys.all }),
  });
}

/** Delete a user, then invalidate the list so it refreshes (D8). */
export function useDeleteUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteUser(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: adminUsersKeys.all }),
  });
}
